import { useEffect, useState } from 'react';
import {
  Combobox, useListCollection, useFilter,
  ProgressCircle,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import type { PortfolioType } from '@customTypes/portfolio';

type Props = {
  errors?: FormErrors | null;
  portfolios: PortfolioType[];
  onSearch: (value: string) => Promise<void>;
  value: PortfolioType | null;
  onChange: (portfolio: PortfolioType | null) => void;
  hideSelectedValue?: boolean;
};

const PortfolioSelect = ({
  errors, portfolios, onSearch, value, onChange, hideSelectedValue = false,
}: Props) => {
  const [searchTimer, setSearchTimer] = useState<ReturnType<typeof setTimeout>>();
  const [searching, setSearching] = useState(false);

  const { contains } = useFilter({ sensitivity: 'base' });

  const { collection, filter, reset, set } = useListCollection({
    initialItems: portfolios,
    itemToValue: (item) => item.id,
    itemToString: (item) => item.name,
    filter: contains,
  });

  const handleInputValueChange = (inputValue: string) => {
    filter(inputValue);
    setSearching(true);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(setTimeout(async () => {
      await onSearch(inputValue);
      setSearching(false);
    }, 1000));
  };

  useEffect(() => {
    if (portfolios) {
      set(portfolios);
    }
  }, [portfolios, set]);

  return (
    <Combobox.Root
      collection={collection}
      value={value ? [value.id] : []}
      onInputValueChange={(e) => handleInputValueChange(e.inputValue)}
      onValueChange={({ items }) => onChange(items[0] || null)}
      invalid={!!errors?.portfolio}
      variant="subtle"
      openOnClick
    >
      <Combobox.Label>Portfólio</Combobox.Label>
      <Combobox.Control>
        <Combobox.Input placeholder="Pesquisar..." value={hideSelectedValue ? '' : undefined} />
        <Combobox.IndicatorGroup>
          {searching && (
            <ProgressCircle.Root value={null} size="xs">
              <ProgressCircle.Circle>
                <ProgressCircle.Track />
                <ProgressCircle.Range />
              </ProgressCircle.Circle>
            </ProgressCircle.Root>
          )}
          <Combobox.ClearTrigger />
          <Combobox.Trigger onClick={reset} />
        </Combobox.IndicatorGroup>
      </Combobox.Control>
      <Combobox.Positioner>
        <Combobox.Content zIndex="popover">
          <Combobox.Empty>Nenhum item encontrado</Combobox.Empty>
          {collection.items.map((item) => (
            <Combobox.Item item={item} key={item.id}>
              {item.name}
              <Combobox.ItemIndicator />
            </Combobox.Item>
          ))}
        </Combobox.Content>
      </Combobox.Positioner>
    </Combobox.Root>
  );
};

export default PortfolioSelect;
